import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ConsentCheckbox, { CONSENT_TEXT, validateConsent } from "@/components/ConsentCheckbox";
import PhoneInput from "@/components/PhoneInput";

export default function AdvisorCallbackForm({ path, testidPrefix, submitLabel = "Request Free Counselling Call" }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", mode: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [consent, setConsent] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.phone.replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter your name and a valid 10-digit mobile number");
      return;
    }
    if (!validateConsent(consent)) return;
    setSending(true);
    try {
      await api.post("/callbacks", {
        name: form.name.trim(),
        phone: form.phone.replace(/\D/g, "").slice(-10),
        email: form.email.trim(),
        program_slug: "",
        preferred_time: form.mode || "Anytime",
        attribution: { source: "landing_page", path, city: "", preferred_mode: form.mode || "" },
        consent_given: consent, consent_text: CONSENT_TEXT,
      });
      setSent(true);
    } catch {
      toast.error("Could not submit — please try again");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-7 text-center" data-testid={`${testidPrefix}-success`}>
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
        <p className="m-0 mt-3 text-lg font-extrabold text-slate-900">Request received!</p>
        <p className="m-0 mt-1 text-sm text-slate-600">Our counsellor will call you back shortly.</p>
      </div>
    );
  }

  const field = "w-full min-h-[48px] px-4 rounded-2xl border border-slate-200 bg-white text-[15px] text-slate-900 outline-none focus:border-[#FFC72C] focus:ring-4 focus:ring-[#FFC72C]/20 transition-all";
  return (
    <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-slate-50 p-5 sm:p-6 grid gap-3.5" data-testid={`${testidPrefix}-form`}>
      <div className="grid sm:grid-cols-2 gap-3.5">
        <label className="grid gap-1.5 text-sm font-bold text-slate-700">Full name
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={field} data-testid={`${testidPrefix}-name`} />
        </label>
        <label className="grid gap-1.5 text-sm font-bold text-slate-700">Mobile number
          <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required inputCls={field} testid={`${testidPrefix}-phone`} />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3.5">
        <label className="grid gap-1.5 text-sm font-bold text-slate-700">Email
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} data-testid={`${testidPrefix}-email`} />
        </label>
        <label className="grid gap-1.5 text-sm font-bold text-slate-700">Preferred mode
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={field} data-testid={`${testidPrefix}-mode`}>
            <option value="">Any mode</option>
            <option>Live online</option>
            <option>Classroom</option>
            <option>Blended</option>
          </select>
        </label>
      </div>
      <ConsentCheckbox checked={consent} onChange={setConsent} testid={`${testidPrefix}-consent`} />
      <button type="submit" disabled={sending} className="min-h-[52px] rounded-full bg-[#FFC72C] hover:bg-[#F5B800] active:scale-[.98] text-[#17263B] font-extrabold transition-all disabled:opacity-60" data-testid={`${testidPrefix}-submit`}>
        {sending ? "Submitting…" : submitLabel}
      </button>
    </form>
  );
}
